import { RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTORS = `
    a[href], 
    area[href], 
    input:not([disabled]),
    select:not([disabled]), 
    textarea:not([disabled]),
    button:not([disabled]),
    [tabindex]:not([tabindex="-1"])
`;

interface UseFocusTrapProps {
    containerRef: RefObject<HTMLElement>;
    onEscape?: () => void;
    focusDelay?: number;
}

export const useFocusTrap = ({ containerRef, onEscape, focusDelay = 10 }: UseFocusTrapProps) => {
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const getFocusableElements = () => {
            return Array.from(
                container.querySelectorAll(FOCUSABLE_SELECTORS)
            ) as HTMLElement[];
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onEscape?.();
                return;
            }

            const isTabPressed = e.key === 'Tab';
            const isShiftPressed = e.shiftKey;

            if (!isTabPressed) return;

            e.preventDefault();

            const focusableElementsArray = getFocusableElements();
            const currentIndex = focusableElementsArray.findIndex(el => el.isSameNode(document.activeElement));
            let nextIndex = (currentIndex + (isShiftPressed ? -1 : 1)) % focusableElementsArray.length;
            if (nextIndex < 0) nextIndex = focusableElementsArray.length - 1;

            focusableElementsArray[nextIndex]?.focus();
        };

        document.addEventListener('keydown', handleKeyDown);

        const delay = focusDelay !== undefined ? focusDelay : 10;
        const timer = setTimeout(() => {
            const focusableElementsArray = getFocusableElements();
            const firstEl = focusableElementsArray[0];
            if (!container.contains(document.activeElement)) {
                firstEl?.focus();
            }
        }, delay);


        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            clearTimeout(timer);
        };
    }, [containerRef, onEscape, focusDelay]);
};
